import { FC } from 'react';
import cn from 'classnames';

import styles from 'components/Sections/OrangeAlert/OrangeAlert.module.scss';
import { WebpImage } from 'components/Shared/SharedComponents';
import { OrangeAlertListItemIconInterface as IconInterface, OrangeAlertIconMultiplier as IconMultiplier } from 'components/Sections/OrangeAlert/declarations';
import { MobileItemsLayout, ViewerDevice } from 'constants/enums';
import {
  EXTERNAL_WEBP_IMAGE,
  SVG_TYPE,
  SVG_XML_TYPE,
} from 'constants/constants';
import { HeightAndWidth, WebpWidthLimitType } from 'types/objectTypes';
import { SidesCalculator } from 'lib/image.service';

const multiplyOnValueWithFloor = (val?: number,
  multiplier: number = IconMultiplier.DEFAULT) => Math
  .floor(val! * multiplier);

const getIconWidthLimit = (
  view: HeightAndWidth<number>,
  media?: string | number,
  rest?: Partial<WebpWidthLimitType>,
) => {
  if (!media) {
    return {} as WebpWidthLimitType;
  }

  return ({
    width: view.width,
    height: view.height,
    media: `${media}px`,
    ...rest,
  });
};

const getSideMultiplier = (
  mobileItemsLayout: MobileItemsLayout,
  device: ViewerDevice = ViewerDevice.DESKTOP,
) => {
  if (mobileItemsLayout === MobileItemsLayout.GRID_2
      || mobileItemsLayout === MobileItemsLayout.GRID_3) {
    if (device === ViewerDevice.DESKTOP) {
      return IconMultiplier.DEFAULT + 0.1;
    }

    if (device === ViewerDevice.TABLET) {
      return IconMultiplier.TABLET + 0.05;
    }
  }

  if (mobileItemsLayout === MobileItemsLayout.GRID_2) {
    return IconMultiplier.MOBILE + 0.2;
  }

  if (mobileItemsLayout === MobileItemsLayout.GRID_3) {
    return IconMultiplier.MOBILE + 0.1;
  }

  if (device === ViewerDevice.DESKTOP) {
    return IconMultiplier.DEFAULT;
  }

  return IconMultiplier.TABLET;
};

/* Nested component */
export const Icon: FC<IconInterface> = ({
  image,
  mobileItemsLayout,
  mobileViewMaxWidth,
  commonTabletMaxWidth,
}) => {
  if (!image) {
    return null;
  }

  if (image.url
    && (image.contentType === SVG_XML_TYPE || image.contentType === SVG_TYPE)) {
    return (
      <WebpImage
        height={image.height!}
        width={image.width!}
        image={EXTERNAL_WEBP_IMAGE}
        src={image.url}
        className={cn(
          styles.image,
          styles.blockBasedImage,
        )}
      />
    );
  }

  const sideCalculator = new SidesCalculator(image, multiplyOnValueWithFloor);

  const desktopMultiplier = getSideMultiplier(mobileItemsLayout);
  const tabletMultiplier = getSideMultiplier(mobileItemsLayout, ViewerDevice.TABLET);
  const mobileMultiplier = getSideMultiplier(mobileItemsLayout, ViewerDevice.MOBILE);

  const desktopIconSids = sideCalculator.calculate(desktopMultiplier);
  const tabletIconSids = sideCalculator.calculate(tabletMultiplier);
  const mobileIconSids = sideCalculator.calculate(mobileMultiplier);

  const widthLimit: Array<WebpWidthLimitType> = [
    getIconWidthLimit(mobileIconSids, mobileViewMaxWidth),
    getIconWidthLimit(tabletIconSids, commonTabletMaxWidth),
    getIconWidthLimit(desktopIconSids, commonTabletMaxWidth, { isMinWidth: true }),
  ];

  return (
    <WebpImage
      image={image}
      widthLimit={widthLimit}
      className={styles.image}
    />
  );
};
